import React, { useState, useContext } from "react";
import PlatformCtx from "../state/PlatformCtx";
import { STAFF_PERM_ACTIONS, STAFF_DEFAULT_PERMS, FACILITY_PERM_ACTIONS, FACILITY_DEFAULT_PERMS, STAFF_VIEW_ACTIONS, STAFF_VIEW_DEFAULT_PERMS, getAllUserProfiles } from "../permissions/checks";
import { NOTIF_EVENTS } from "../notifications";
import PermissionDebugPanel from "./PermissionDebugPanel";

export default function StaffAuthoritiesTab({state,dispatch,t}){
  const platformCtx=useContext(PlatformCtx);
  const {users,staffAuthorities,staffRoles,staffPermissions,staffViewPermissions,lang,notifPrefs}=state;
  const isRTL=lang==="ar";
  const auth = staffAuthorities || {salaryApprovers:[]};
  const [newRole,setNewRole]=useState("");
  const profiles=platformCtx?.platformUserProfiles||[];
  const platStaffPerms=platformCtx?.platformStaffPermissions||{};
  const wsPerms=staffPermissions||{};
  const effectivePerms={...STAFF_DEFAULT_PERMS,...platStaffPerms,...wsPerms};

  /* ── Staff-workspace view permissions (who sees which tab) ── */
  const allProfiles=getAllUserProfiles(state,platformCtx);
  const staffProfiles=allProfiles.filter(p=>p.group==="Staff");
  const platStaffViewPerms=platformCtx?.platformStaffViewPermissions||{};
  const wsViewPerms=staffViewPermissions||{};
  const effectiveViewPerms={...STAFF_VIEW_DEFAULT_PERMS,...platStaffViewPerms,...wsViewPerms};
  /* "*staff" in the effective list means every Staff-group role is
     allowed. Render all checkboxes as ON for that row. When the owner
     toggles any checkbox, expand the wildcard into an explicit list
     (all current Staff-group role ids) first, then apply the toggle —
     so switching off a single role doesn't silently drop the others. */
  const isRoleChecked=(action,roleId)=>{
    const list=effectiveViewPerms[action]||[];
    if(list.includes("*staff"))return true;
    return list.includes(roleId);
  };
  const toggleViewPerm=(action,roleId)=>{
    const current=effectiveViewPerms[action]||[];
    const expanded=current.includes("*staff")
      ?staffProfiles.map(p=>p.id)
      :current;
    const next=expanded.includes(roleId)?expanded.filter(x=>x!==roleId):[...expanded,roleId];
    dispatch({type:"SET_STAFF_VIEW_PERMS",patch:{[action]:next}});
  };
  const hasWsViewOverrides=Object.keys(wsViewPerms).length>0;
  /* Tab labels — role-agnostic, so any staff role's access to a tab is a
     clear yes/no decision. Mirrors the manifest in StaffView.jsx. */
  const STAFF_VIEW_LABELS=[
    {action:"viewOwnRides",         label:isRTL?"رؤية رحلاتي":"Access My Rides"},
    {action:"fulfillGroceryRuns",   label:isRTL?"تنفيذ طلبات البقالة":"Fulfill Grocery Runs"},
    {action:"requestGroceries",     label:isRTL?"طلب بقالة":"Request Groceries"},
    {action:"viewOwnGroceryHistory",label:isRTL?"سجل البقالة الخاص":"Access Grocery History"},
    {action:"logOwnExpenses",       label:isRTL?"تسجيل المصروفات":"Log Own Expenses"},
    {action:"viewStaffSuppliers",   label:isRTL?"عرض الموردين":"View Supplier Directory"},
  ];

  const togglePerm=(action,profileId)=>{
    const current=effectivePerms[action]||[];
    const next=current.includes(profileId)?current.filter(x=>x!==profileId):[...current,profileId];
    dispatch({type:"SET_STAFF_PERMISSIONS",patch:{[action]:next}});
  };

  const toggleSalaryNotify=(userId)=>{
    const arr=auth.salaryApprovers||[];
    const next=arr.includes(userId)?arr.filter(x=>x!==userId):[...arr,userId];
    dispatch({type:"SET_STAFF_AUTHORITIES",patch:{salaryApprovers:next}});
  };

  const allUsers=users.filter(u=>u.active);
  const getProfile=(roleId)=>profiles.find(p=>p.id===roleId);

  // Protected roles — linked to a platformUserProfile
  const protectedRoles=new Set(profiles.filter(p=>p.group==="Staff").map(p=>p.name.toLowerCase()));

  const PERM_LABELS=[
    {action:"viewStaff",label:t.canViewStaff},
    {action:"addStaff",label:t.canAddStaff},
    {action:"editStaff",label:t.canEditStaff},
    {action:"managePayroll",label:t.canManagePayroll},
    {action:"approveLeave",label:t.canApproveLeave},
    {action:"approveLoans",label:t.canApproveLoans},
    {action:"manageDocs",label:t.canManageDocs},
  ];

  const hasWsOverrides=Object.keys(wsPerms).length>0;

  // Staff notification events — derived from NOTIF_EVENTS to stay in sync with SA
  const STAFF_NOTIF_EVENTS=NOTIF_EVENTS.filter(e=>e.group==="Staff").map(e=>({key:e.key,label:isRTL?({salary_processed:"تم معالجة الراتب",loan_disbursed:"تم صرف قرض",leave_requested:"طلب إجازة",leave_approved:"إجازة موافق عليها"}[e.key]||e.label):e.label}));

  return(
    <div data-onboarding-target="staff-authorities-tab" style={{display:"flex",flexDirection:"column",gap:18}}>

      {/* ── Staff Permissions Matrix ── */}
      <div className="card">
        <div className="ch">
          <div>
            <div className="ct">{t.staffPermissions}</div>
            <div style={{fontSize:11,color:"var(--g400)",marginTop:3,lineHeight:1.6,maxWidth:480}}>
              {isRTL?"تحكم في صلاحيات كل ملف مستخدم. المستخدمون الذين لديهم صلاحية الموافقة على الإجازات/القروض يتم إشعارهم تلقائياً عند تقديم طلب.":"Control what each user profile can do. Users with leave/loan approval permissions are automatically notified when requests are submitted."}
            </div>
          </div>
          {hasWsOverrides&&<button className="btn btn-g btn-xs" onClick={()=>dispatch({type:"SET_STAFF_PERMISSIONS",patch:Object.fromEntries(STAFF_PERM_ACTIONS.map(a=>[a,undefined]))})}>{t.resetToDefault}</button>}
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{borderBottom:"1px solid var(--g200)"}}>
                <th style={{textAlign:isRTL?"right":"left",padding:"8px 12px",fontSize:11,color:"var(--g500)"}}>{isRTL?"الإجراء":"Action"}</th>
                {profiles.filter(p=>p.id!=="owner").map(p=>(
                  <th key={p.id} style={{textAlign:"center",padding:"8px 6px",fontSize:10,color:"var(--g500)",fontWeight:500}}>
                    {isRTL&&p.nameAr?p.nameAr:p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERM_LABELS.map(({action,label})=>(
                <tr key={action} style={{borderBottom:"1px solid var(--g100)"}}>
                  <td style={{padding:"8px 12px",fontWeight:500,color:"var(--g700)"}}>{label}</td>
                  {profiles.filter(p=>p.id!=="owner").map(p=>{
                    const checked=(effectivePerms[action]||[]).includes(p.id);
                    return(
                      <td key={p.id} style={{textAlign:"center",padding:"6px"}}>
                        <div className={`toggle ${checked?"on":""}`} style={{margin:"0 auto",transform:"scale(0.8)"}}
                          onClick={()=>togglePerm(action,p.id)}/>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{padding:"8px 12px",fontSize:11,color:"var(--g400)",borderTop:"1px solid var(--g100)"}}>
          {isRTL?"المالك لديه صلاحية كاملة دائماً. المستخدمون الذين لديهم صلاحية الموافقة على الإجازات/القروض يتلقون الإشعارات تلقائياً.":"The owner always has full access. Users with leave/loan approval permissions automatically receive notifications."}
        </div>
      </div>

      {/* ── Staff Workspace Access ── Role × tab matrix for the unified
         StaffView. My Tasks / My Leave / My Info are universal (always
         on) so they aren't listed — the grid focuses on opt-in tabs. */}
      <div className="card">
        <div className="ch">
          <div>
            <div className="ct">{isRTL?"صلاحيات شاشة الموظف":"Staff Workspace Access"}</div>
            <div style={{fontSize:11,color:"var(--g400)",marginTop:3,lineHeight:1.6,maxWidth:520}}>
              {isRTL
                ?"تحكم في الأقسام التي يراها كل دور في مساحة عمل الموظف. المهام والإجازات ومعلوماتي مفعلة دائماً لكل موظف."
                :"Choose which tabs each staff role sees in their workspace. My Tasks, My Leave, and My Info are always enabled for every staff user."}
            </div>
          </div>
          {hasWsViewOverrides&&<button className="btn btn-g btn-xs" onClick={()=>dispatch({type:"SET_STAFF_VIEW_PERMS",patch:Object.fromEntries(STAFF_VIEW_ACTIONS.map(a=>[a,undefined]))})}>{t.resetToDefault}</button>}
        </div>
        {staffProfiles.length===0?(
          <div style={{padding:"14px 18px",fontSize:12,color:"var(--g400)"}}>
            {isRTL?"لا توجد أدوار موظفين. أضف دوراً أدناه لبدء التخصيص.":"No staff roles yet. Add one below to start customizing access."}
          </div>
        ):(
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{borderBottom:"1px solid var(--g200)"}}>
                  <th style={{textAlign:isRTL?"right":"left",padding:"8px 12px",fontSize:11,color:"var(--g500)"}}>{isRTL?"القسم":"Tab"}</th>
                  {staffProfiles.map(p=>(
                    <th key={p.id} style={{textAlign:"center",padding:"8px 6px",fontSize:10,color:"var(--g500)",fontWeight:500}}>
                      <div>{isRTL&&p.nameAr?p.nameAr:p.name}</div>
                      {p.source==="workspace"&&<div style={{fontSize:8,color:"var(--g400)",marginTop:2,fontWeight:400}}>{isRTL?"مساحة عمل":"Workspace"}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {STAFF_VIEW_LABELS.map(({action,label})=>(
                  <tr key={action} style={{borderBottom:"1px solid var(--g100)"}}>
                    <td style={{padding:"8px 12px",fontWeight:500,color:"var(--g700)"}}>{label}</td>
                    {staffProfiles.map(p=>{
                      const checked=isRoleChecked(action,p.id);
                      return(
                        <td key={p.id} style={{textAlign:"center",padding:"6px"}}>
                          <div className={`toggle ${checked?"on":""}`} style={{margin:"0 auto",transform:"scale(0.8)"}}
                            onClick={()=>toggleViewPerm(action,p.id)}/>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{padding:"8px 12px",fontSize:11,color:"var(--g400)",borderTop:"1px solid var(--g100)"}}>
          {isRTL?"المالك ليس ظاهراً في الجدول — يرى كل شيء دائماً.":"The owner is not shown — always sees everything."}
        </div>
      </div>

      {/* Facility Permissions */}
      <div className="card">
        <div className="ch">
          <div>
            <div className="ct">{t.facilityPerms}</div>
            <div style={{fontSize:11,color:"var(--g400)",marginTop:3,lineHeight:1.6,maxWidth:420}}>{t.facilityPermsDesc}</div>
          </div>
          {Object.keys(state.facilityPermissions||{}).length>0&&<button className="btn btn-g btn-xs" onClick={()=>dispatch({type:"SET_FACILITY_PERMS",patch:Object.fromEntries(FACILITY_PERM_ACTIONS.map(a=>[a,undefined]))})}>{t.resetToDefault}</button>}
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{borderBottom:"1px solid var(--g200)"}}>
                <th style={{textAlign:isRTL?"right":"left",padding:"8px 12px",fontSize:11,color:"var(--g500)"}}>{isRTL?"الإجراء":"Action"}</th>
                {profiles.filter(p=>p.id!=="owner").map(p=>(
                  <th key={p.id} style={{textAlign:"center",padding:"8px 6px",fontSize:10,color:"var(--g500)",fontWeight:500}}>
                    {isRTL&&p.nameAr?p.nameAr:p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[{action:"viewMaintenance",label:t.canViewMaint},{action:"addMaintenance",label:t.canAddMaint},{action:"editMaintenance",label:t.canEditMaint},{action:"manageMaintenance",label:t.canManageMaint}].map(({action,label})=>{
                const facPerms=state.facilityPermissions||FACILITY_DEFAULT_PERMS;
                return(
                <tr key={action} style={{borderBottom:"1px solid var(--g100)"}}>
                  <td style={{padding:"8px 12px",fontWeight:500,color:"var(--g700)"}}>{label}</td>
                  {profiles.filter(p=>p.id!=="owner").map(p=>{
                    const checked=(facPerms[action]||[]).includes(p.id);
                    return(
                      <td key={p.id} style={{textAlign:"center",padding:"6px"}}>
                        <div className={`toggle ${checked?"on":""}`} style={{margin:"0 auto",transform:"scale(0.8)"}}
                          onClick={()=>{const cur=facPerms[action]||[];const next=cur.includes(p.id)?cur.filter(x=>x!==p.id):[...cur,p.id];dispatch({type:"SET_FACILITY_PERMS",patch:{[action]:next}});}}/>
                      </td>
                    );
                  })}
                </tr>
              );})}
            </tbody>
          </table>
        </div>
        <div style={{padding:"8px 12px",fontSize:11,color:"var(--g400)",borderTop:"1px solid var(--g100)"}}>
          {isRTL?"المالك لديه صلاحية كاملة دائماً ولا يظهر في الجدول.":"The owner always has full access and is not shown in the table."}
        </div>
      </div>

      {/* ── Salary Change Notification ── */}
      <div className="card">
        <div className="ch">
          <div>
            <div className="ct">{t.salaryReviewers}</div>
            <div style={{fontSize:11,color:"var(--g400)",marginTop:3,lineHeight:1.6,maxWidth:420}}>{t.salaryReviewersDesc}</div>
          </div>
        </div>
        <div style={{padding:"4px 0"}}>
          {allUsers.map(u=>{
            const prof=getProfile(u.role);
            const isOwner=prof?.id==="owner";
            const checked=isOwner||(auth.salaryApprovers||[]).includes(u.id);
            return(
              <div key={u.id} style={{display:"flex",alignItems:"center",gap:12,padding:"9px 18px",borderBottom:"1px solid var(--g100)"}}>
                <div style={{width:30,height:30,borderRadius:"50%",background:isOwner?"var(--ink)":"var(--g100)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--fm)",fontSize:10,fontWeight:600,color:isOwner?"white":"var(--g600)",flexShrink:0}}>{u.initials||u.name.slice(0,2).toUpperCase()}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13.5,fontWeight:500}}>{isRTL&&u.nameAr?u.nameAr:u.name}</div>
                  <div style={{fontSize:11,color:"var(--g400)",textTransform:"capitalize"}}>{prof?(isRTL&&prof.nameAr?prof.nameAr:prof.name):u.role}{isOwner?` · ${t.alwaysIncluded}`:""}</div>
                </div>
                <div className={`toggle ${checked?"on":""}`}
                  style={{opacity:isOwner?0.35:1,pointerEvents:isOwner?"none":"auto"}}
                  onClick={()=>!isOwner&&toggleSalaryNotify(u.id)}/>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Staff Notification Preferences ── */}
      <div className="card">
        <div className="ch">
          <div>
            <div className="ct">{isRTL?"إشعارات الموظفين":"Staff Notifications"}</div>
            <div style={{fontSize:11,color:"var(--g400)",marginTop:3,lineHeight:1.6,maxWidth:420}}>
              {isRTL?"اختر قنوات الإشعارات لأحداث الموظفين.":"Choose notification channels for staff events."}
            </div>
          </div>
        </div>
        <div style={{padding:"4px 0"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 60px 60px",gap:0,padding:"6px 18px",borderBottom:"1px solid var(--g200)"}}>
            <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase"}}>{isRTL?"الحدث":"Event"}</div>
            <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",textAlign:"center"}}>Email</div>
            <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",textAlign:"center"}}>WhatsApp</div>
          </div>
          {STAFF_NOTIF_EVENTS.map(ev=>{
            const pref=(notifPrefs||{})[ev.key]||{};
            return(
              <div key={ev.key} style={{display:"grid",gridTemplateColumns:"1fr 60px 60px",gap:0,padding:"9px 18px",borderBottom:"1px solid var(--g100)",alignItems:"center"}}>
                <div style={{fontSize:13,fontWeight:500,color:"var(--g700)"}}>{ev.label}</div>
                <div style={{textAlign:"center"}}>
                  <div className={`toggle ${pref.email!==false?"on":""}`} style={{margin:"0 auto",transform:"scale(0.75)"}}
                    onClick={()=>dispatch({type:"SET_NOTIF_PREF",key:ev.key,patch:{email:pref.email===false?true:false}})}/>
                </div>
                <div style={{textAlign:"center"}}>
                  <div className={`toggle ${pref.whatsapp?"on":""}`} style={{margin:"0 auto",transform:"scale(0.75)"}}
                    onClick={()=>dispatch({type:"SET_NOTIF_PREF",key:ev.key,patch:{whatsapp:!pref.whatsapp}})}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Staff Roles ── */}
      <div className="card">
        <div className="ch"><div className="ct">{t.staffRolesLabel}</div></div>
        <div style={{padding:"12px 18px"}}>
          <div style={{fontSize:11,color:"var(--g400)",marginBottom:10,lineHeight:1.6}}>{t.staffRolesDesc}</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
            {(staffRoles||[]).map(r=>{
              const isProtected=protectedRoles.has(r.toLowerCase());
              return(
                <div key={r} style={{display:"flex",alignItems:"center",gap:6,background:isProtected?"var(--blu-bg)":"var(--g50)",border:`1px solid ${isProtected?"var(--blu-bd)":"var(--g200)"}`,borderRadius:20,padding:"4px 6px 4px 12px"}}>
                  <span style={{fontSize:12,fontWeight:500,textTransform:"capitalize",color:"var(--g700)"}}>{r}</span>
                  {isProtected
                    ?<span style={{fontSize:9,color:"var(--blu)",marginInlineStart:2}}>🔒</span>
                    :<button onClick={()=>dispatch({type:"REMOVE_STAFF_ROLE",role:r})}
                      style={{width:18,height:18,borderRadius:"50%",border:"1px solid var(--g300)",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"var(--g400)",lineHeight:1}}>×</button>
                  }
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",gap:8}}>
            <input className="fi" value={newRole} onChange={e=>setNewRole(e.target.value)} placeholder={t.newRoleName} style={{flex:1}}
              onKeyDown={e=>{if(e.key==="Enter"&&newRole.trim()&&!(staffRoles||[]).includes(newRole.trim())){dispatch({type:"ADD_STAFF_ROLE",role:newRole.trim()});setNewRole("");}}}/>
            <button className="btn btn-p btn-sm" onClick={()=>{if(newRole.trim()&&!(staffRoles||[]).includes(newRole.trim())){dispatch({type:"ADD_STAFF_ROLE",role:newRole.trim()});setNewRole("");}}}>{t.addRole}</button>
          </div>
          {protectedRoles.size>0&&<div style={{fontSize:11,color:"var(--g400)",marginTop:8}}>
            🔒 {isRTL?"الأدوار المرتبطة بملفات المستخدمين محمية ولا يمكن حذفها.":"Roles linked to user profiles are protected and cannot be deleted."}
          </div>}
        </div>
      </div>

      {/* ── Info notices ── */}
      <div className="notice">
        <div className="notice-l">{t.howItWorks}</div>
        {isRTL?"عند تقديم طلب إجازة أو قرض، يتم إرسال إشعار لكل مستخدم لديه صلاحية الموافقة. المالك مضمّن دائماً. مراجعو الرواتب يتم إشعارهم فقط عند تغيير الراتب.":"When a leave or loan request is submitted, every user whose profile has the corresponding approval permission gets notified. The owner is always included. Salary reviewers are notified only on salary changes."}
      </div>

      {/* ── Permission Resolution Debug (owner only) ── */}
      {(users||[]).find(u=>u.role==="owner")&&(
        <PermissionDebugPanel state={state} platformCtx={platformCtx} isRTL={isRTL}/>
      )}
    </div>
  );
}
